import { useState } from "react";
import { Mail, X, CheckCircle, Loader2 } from "lucide-react";
import { useUser } from "../contexts/UserContext";
import { API } from "../lib/constants";

export default function EmailVerificationBanner() {
  const { user } = useUser();
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  // Only show for signed-in users with an explicit `email_verified === false`.
  // Old accounts without the field default to verified (no banner spam).
  if (!user || user.email_verified !== false || dismissed) return null;

  const handleResend = async () => {
    setSending(true);
    try {
      await fetch(`${API}/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: user.email }),
      });
      setSent(true);
    } catch {
      // silent — uniform UX
      setSent(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed top-16 left-1/2 -translate-x-1/2 z-40 w-[92%] max-w-2xl bg-amber-950/95 backdrop-blur-md border border-amber-500/40 rounded-lg shadow-xl shadow-amber-900/20 px-4 py-3"
      data-testid="verify-email-banner"
    >
      <div className="flex items-center gap-3">
        <Mail size={18} className="text-amber-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          {sent ? (
            <p className="text-xs sm:text-sm text-amber-100 flex items-center gap-2">
              <CheckCircle size={14} className="text-green-400 flex-shrink-0" />
              Verification email sent. Please check your inbox.
            </p>
          ) : (
            <p className="text-xs sm:text-sm text-amber-100">
              <span className="font-bold">Verify your email</span> to unlock account recovery and full features.
            </p>
          )}
        </div>
        {!sent && (
          <button
            onClick={handleResend}
            disabled={sending}
            className="px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold rounded transition-colors disabled:opacity-50 flex items-center gap-1.5"
            data-testid="resend-verification-btn"
          >
            {sending ? <Loader2 size={12} className="animate-spin" /> : null}
            {sending ? "Sending…" : "Resend"}
          </button>
        )}
        <button
          onClick={() => setDismissed(true)}
          className="p-1 hover:bg-amber-500/20 rounded transition-colors"
          aria-label="Dismiss"
          data-testid="dismiss-verification-banner"
        >
          <X size={14} className="text-amber-300" />
        </button>
      </div>
    </div>
  );
}
